/**
 * Member invite-code redemption (KBT-174).
 *
 * Deliberately mirrors phoneVerification.js's shape — a validate* helper,
 * an input sanitizer, and a network call that returns a consistent
 * { success, message, ... } object instead of throwing — so this flow
 * feels like the same family of code to anyone who's already read the
 * phone verification service.
 *
 * One difference from phoneVerification.js: the invite code's expiry,
 * attempt-limiting, and one-time-use state all live server-side (see
 * kbt-backend/invites/invites.service.js), so there's no localStorage
 * bookkeeping here — every call just relays what POST /invites/verify
 * says.
 */

const CODE_LENGTH = 6;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateEmail = (email) => {
  if (!email) return { valid: false, error: 'Email is required' };
  const trimmed = String(email).trim();
  if (!trimmed) return { valid: false, error: 'Email is required' };
  if (!EMAIL_REGEX.test(trimmed)) return { valid: false, error: 'Invalid email address' };
  return { valid: true, formatted: trimmed.toLowerCase() };
};

export const sanitizeCodeInput = (raw) => (raw || '').replace(/\D/g, '').slice(0, CODE_LENGTH);

export const isCodeComplete = (code) => /^\d{6}$/.test(code || '');

/**
 * POST /invites/verify. Returns:
 *   success -> { success: true, email, inviteId, quickBooksCustomerId, quickBooksCustomerName }
 *   failure -> { success: false, message, expired?, rateLimited? }
 * Never throws — callers can render `result.message` directly, same
 * pattern as verifyPhoneOTP/sendPhoneOTP.
 */
export const verifyInviteCode = async (email, code) => {
  const emailCheck = validateEmail(email);
  if (!emailCheck.valid) {
    return { success: false, message: emailCheck.error };
  }

  const baseUrl = process.env.REACT_APP_BASE_URL;
  if (!baseUrl) {
    return { success: false, message: 'REACT_APP_BASE_URL is not set. Check .env.' };
  }

  try {
    const response = await fetch(`${baseUrl}/invites/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailCheck.formatted, code }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data.msg || data.message || 'Invalid code';
      return {
        success: false,
        message,
        expired: response.status === 410,
        rateLimited: response.status === 429,
      };
    }

    return {
      success: true,
      email: data.email,
      inviteId: data.inviteId,
      quickBooksCustomerId: data.quickBooksCustomerId,
      quickBooksCustomerName: data.quickBooksCustomerName,
    };
  } catch (error) {
    console.error('Verify invite code error:', error);
    return { success: false, message: 'Failed to verify code. Please check your connection and try again.' };
  }
};

const inviteVerificationService = {
  validateEmail,
  sanitizeCodeInput,
  isCodeComplete,
  verifyInviteCode,
};

export default inviteVerificationService;
